import type { HTMLImage } from 'lane-shared/types/media';
import {
  isBase64MediaPayload,
  type Base64MediaPayload,
} from 'application-types';
import { MediaImageContentTypeEnum } from 'constants-media';

import {
  fileToBase64,
  formatDateTakenForExif,
  isHeicFile,
  readOriginalDateTaken,
  renameHeicExtensionToJpeg,
} from './images/heicFile';

export enum FileReturnTypeEnum {
  File = 'file',
  Text = 'text',
  Image = 'image',
  ImageUrl = 'image-url',
  JSON = 'json',
}

export type FileReturnType = (
  | File
  | HTMLImage
  | string
  | Base64MediaPayload
) & {
  _id?: string;
};

export { isBase64MediaPayload as isHeicUploadPayload };

export default async function fileReaderResolver(
  file: File,
  type: FileReturnTypeEnum,
  // Opt-in only, default false — this helper is shared by every FileInput/
  // FileDrop consumer in the app, most of which haven't rolled out HEIC
  // support yet. Gated behind FeatureFlag.HeicMediaUpload by callers.
  heicUploadEnabled = false
): Promise<FileReturnType> {
  // HEIC decode happens server-side (worker_thread) — skip client-side
  // decode entirely and ship the raw bytes plus the EXIF date, which is
  // still readable client-side via exifr.
  if (heicUploadEnabled && type === 'image' && isHeicFile(file)) {
    const [fileBase64, dateTaken] = await Promise.all([
      fileToBase64(file),
      readOriginalDateTaken(file),
    ]);

    const payload: Base64MediaPayload = {
      name: renameHeicExtensionToJpeg(file.name),
      size: file.size,
      // MediaContentType is a closed GraphQL enum — 'image/heic' isn't a
      // member and gets rejected before the resolver even runs. Send the
      // real final type (this file WILL end up as a JPEG); fileBase64's
      // presence alone is what signals server-side conversion is needed.
      type: MediaImageContentTypeEnum.jpeg,
      fileBase64,
      dateTaken: dateTaken ? formatDateTakenForExif(dateTaken) : undefined,
    };

    return payload;
  }

  return new Promise((resolve, reject) => {
    const fileReader = new FileReader();

    fileReader.onload = () => {
      switch (type) {
        case 'image': {
          const img = new Image() as HTMLImage;

          img.src = fileReader.result as string;
          img.name = file.name;
          img.size = file.size;
          img.type = file.type;
          img.onload = () => {
            resolve(img);
          };
          // Without this, a decode failure leaves the promise hanging forever
          // instead of surfacing an error (this is what happened with HEIC).
          img.onerror = () => {
            reject(new Error(`Failed to load image "${file.name}"`));
          };

          return;
        }

        case 'json':
        case 'text':
        case 'image-url':
        default:
          if (!fileReader.result) {
            reject(new Error('File Empty'));

            return;
          }

          resolve(fileReader.result.toString());
      }
    };

    fileReader.onabort = reject;
    fileReader.onerror = reject;

    if (['text', 'json'].includes(type)) {
      fileReader.readAsText(file);
    } else {
      fileReader.readAsDataURL(file);
    }
  });
}
